"use client";

import { useCallback, useEffect, useState } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  PLACEMENTS,
  SHARE_PLATFORMS,
  trackChatShare,
} from "@/lib/analytics";
import { CheckCircleFillIcon, CopyIcon, ShareIcon } from "./icons";
import { toast } from "./toast";

type SharePlatform = "x" | "telegram" | "copy" | "native";

type ShareButtonsProps = {
  chatId: string;
  title: string;
  className?: string;
};

type ShortLinkResponse = {
  shortUrl: string;
  code: string;
  title: string;
  isExisting?: boolean;
  error?: string;
};

// X (Twitter) icon
function XIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      fill="currentColor"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// Telegram icon
function TelegramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      fill="currentColor"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

export function ShareButtons({ chatId, title, className }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();

  // Check for native share support on client side only
  useEffect(() => {
    setCanNativeShare(typeof navigator !== "undefined" && "share" in navigator);
  }, []);

  // Get or create a short link for the chat
  // This also sets the chat to public and returns the actual chat title
  const getShortLink =
    useCallback(async (): Promise<ShortLinkResponse | null> => {
      try {
        const response = await fetch("/api/short-links", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            targetType: "chat",
            targetId: chatId,
            setPublic: true, // Auto-set chat to public when sharing
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || "Failed to create share link");
        }

        return await response.json();
      } catch (error) {
        console.error("Failed to get short link:", error);
        return null;
      }
    }, [chatId]);

  const trackShareEvent = async (platform: SharePlatform) => {
    // Track via GA4/GTM
    const ga4Platform =
      platform === "x"
        ? SHARE_PLATFORMS.TWITTER
        : platform === "telegram"
          ? SHARE_PLATFORMS.TELEGRAM
          : platform === "copy"
            ? SHARE_PLATFORMS.COPY
            : SHARE_PLATFORMS.NATIVE;

    trackChatShare({
      chat_id: chatId,
      platform: ga4Platform,
      placement: PLACEMENTS.HEADER,
    });

    // Also track via database API for persistence
    try {
      await fetch("/api/analytics/share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chatId, platform }),
      });
    } catch {
      // Silent fail - don't block share action
    }
  };

  const handleXShare = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShareEvent("x");
      const shareTitle = linkData.title || title;
      const text = encodeURIComponent(
        `${shareTitle} - Check out this blockchain analysis on TXGPT`
      );
      const url = encodeURIComponent(linkData.shortUrl);
      window.open(
        `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
        "_blank",
        "noopener,noreferrer"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTelegramShare = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShareEvent("telegram");
      const shareTitle = linkData.title || title;
      const text = encodeURIComponent(
        `${shareTitle} - Check out this blockchain analysis on TXGPT`
      );
      const url = encodeURIComponent(linkData.shortUrl);
      window.open(
        `https://t.me/share/url?url=${url}&text=${text}`,
        "_blank",
        "noopener,noreferrer"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShareEvent("copy");
      await copyToClipboard(linkData.shortUrl);
      setCopied(true);
      toast({ type: "success", description: "Link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      return;
    }

    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShareEvent("native");
      const shareTitle = linkData.title || title;
      await navigator.share({
        title: `${shareTitle} | TXGPT`,
        text: "Check out this blockchain analysis on TXGPT",
        url: linkData.shortUrl,
      });
    } catch {
      // User cancelled or error - silent fail
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              className={className}
              disabled={isLoading}
              size="icon"
              variant="outline"
            >
              <ShareIcon size={16} />
              <span className="sr-only">Share</span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Share chat</TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          className="cursor-pointer gap-3"
          disabled={isLoading}
          onClick={handleXShare}
        >
          <XIcon size={16} />
          <span>Share on X</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer gap-3"
          disabled={isLoading}
          onClick={handleTelegramShare}
        >
          <TelegramIcon size={16} />
          <span>Share on Telegram</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer gap-3"
          disabled={isLoading}
          onClick={handleCopyLink}
        >
          {copied ? <CheckCircleFillIcon size={16} /> : <CopyIcon size={16} />}
          <span>{copied ? "Link Copied!" : "Copy Link"}</span>
        </DropdownMenuItem>

        {canNativeShare && (
          <DropdownMenuItem
            className="cursor-pointer gap-3"
            disabled={isLoading}
            onClick={handleNativeShare}
          >
            <ShareIcon size={16} />
            <span>More Options</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Compact inline share buttons for prominent placement
export function ShareButtonsInline({
  chatId,
  title,
  className,
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();

  // Get or create a short link for the chat
  const getShortLink =
    useCallback(async (): Promise<ShortLinkResponse | null> => {
      try {
        const response = await fetch("/api/short-links", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            targetType: "chat",
            targetId: chatId,
            setPublic: true,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || "Failed to create share link");
        }

        return await response.json();
      } catch (error) {
        console.error("Failed to get short link:", error);
        return null;
      }
    }, [chatId]);

  const trackShareEventInline = async (platform: SharePlatform) => {
    // Track via GA4/GTM
    const ga4Platform =
      platform === "x"
        ? SHARE_PLATFORMS.TWITTER
        : platform === "telegram"
          ? SHARE_PLATFORMS.TELEGRAM
          : SHARE_PLATFORMS.COPY;

    trackChatShare({
      chat_id: chatId,
      platform: ga4Platform,
      placement: PLACEMENTS.HEADER,
    });

    // Also track via database API for persistence
    try {
      await fetch("/api/analytics/share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chatId, platform }),
      });
    } catch {
      // Silent fail
    }
  };

  const handleXShare = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShareEventInline("x");
      const shareTitle = linkData.title || title;
      const text = encodeURIComponent(
        `${shareTitle} - Check out this blockchain analysis on TXGPT`
      );
      const url = encodeURIComponent(linkData.shortUrl);
      window.open(
        `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
        "_blank",
        "noopener,noreferrer"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTelegramShare = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShareEventInline("telegram");
      const shareTitle = linkData.title || title;
      const text = encodeURIComponent(
        `${shareTitle} - Check out this blockchain analysis on TXGPT`
      );
      const url = encodeURIComponent(linkData.shortUrl);
      window.open(
        `https://t.me/share/url?url=${url}&text=${text}`,
        "_blank",
        "noopener,noreferrer"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    setIsLoading(true);
    try {
      const linkData = await getShortLink();
      if (!linkData) {
        toast({ type: "error", description: "Failed to create share link" });
        return;
      }

      await trackShareEventInline("copy");
      await copyToClipboard(linkData.shortUrl);
      setCopied(true);
      toast({ type: "success", description: "Link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex items-center gap-1 ${className || ""}`}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className="h-8 w-8"
            disabled={isLoading}
            onClick={handleXShare}
            size="icon"
            variant="ghost"
          >
            <XIcon size={14} />
            <span className="sr-only">Share on X</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Share on X</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className="h-8 w-8"
            disabled={isLoading}
            onClick={handleTelegramShare}
            size="icon"
            variant="ghost"
          >
            <TelegramIcon size={14} />
            <span className="sr-only">Share on Telegram</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Share on Telegram</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className="h-8 w-8"
            disabled={isLoading}
            onClick={handleCopyLink}
            size="icon"
            variant="ghost"
          >
            {copied ? (
              <CheckCircleFillIcon size={14} />
            ) : (
              <CopyIcon size={14} />
            )}
            <span className="sr-only">{copied ? "Copied" : "Copy link"}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Link Copied!" : "Copy Link"}</TooltipContent>
      </Tooltip>
    </div>
  );
}
